import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TaxFormWizard } from "./TaxFormWizard";
import {
  type TaxProfileSummary, formatStatus, getMyTaxProfile,
} from "@/lib/affiliateTax";

export function TaxInformationCard() {
  const [profile, setProfile] = useState<TaxProfileSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setProfile(await getMyTaxProfile()); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const status = profile?.status;
  const fmt = status ? formatStatus(status) : null;
  const expiresSoon = profile?.expires_at && new Date(profile.expires_at) < new Date(Date.now() + 1000 * 60 * 60 * 24 * 60);

  const ctaLabel = !profile
    ? "Add tax information"
    : status === "verified"
      ? "Update tax information"
      : status === "rejected"
        ? "Fix and resubmit"
        : status === "submitted"
          ? "View submission"
          : "Continue draft";

  const canEdit = !profile || status === "draft" || status === "rejected" || status === "verified" || status === "expired";

  return (
    <>
      <Card className="shadow-soft">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> Tax information
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                Required before commission payouts. W-9 (US) or W-8BEN (non-US).
              </p>
            </div>
            {fmt ? (
              <Badge variant={fmt.tone === "success" ? "default" : fmt.tone === "warning" ? "secondary" : "destructive"}>
                {fmt.label}
              </Badge>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <Skeleton className="h-16 w-full" />
          ) : profile ? (
            <div className="space-y-1.5 text-sm">
              <div className="text-muted-foreground">
                {profile.form_type === "w9" ? "W-9" : "W-8BEN"} · {profile.legal_name}
                {profile.business_name ? ` (${profile.business_name})` : ""}
              </div>
              {profile.tin_last4 ? (
                <div className="text-muted-foreground">TIN ending in {profile.tin_last4}</div>
              ) : null}
              {profile.address_preview ? (
                <div className="text-muted-foreground">{profile.address_preview}</div>
              ) : null}
              {status === "verified" && profile.expires_at ? (
                <div className={expiresSoon ? "text-destructive" : "text-muted-foreground"}>
                  Verified · expires {new Date(profile.expires_at).toLocaleDateString()}
                </div>
              ) : null}
              {status === "rejected" && profile.rejected_reason ? (
                <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{profile.rejected_reason}</span>
                </div>
              ) : null}
              {status === "submitted" ? (
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4" /> Submitted — typically reviewed within 2 business days.
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No tax form on file. Submit one now so payouts aren't delayed.
            </p>
          )}
          {canEdit ? (
            <Button onClick={() => setOpen(true)} variant={profile ? "outline" : "default"}>
              {ctaLabel}
            </Button>
          ) : null}
        </CardContent>
      </Card>

      <TaxFormWizard
        open={open}
        onOpenChange={setOpen}
        existing={profile}
        onSubmitted={load}
      />
    </>
  );
}
